import { Consultation } from '@/core/models/consultation.model'
import { TranscriptionSegment, TranscriptionSpeaker } from '@/core/models/transcription'

export type LiveTranscriptStatus = 'idle' | 'connecting' | 'listening' | 'stopping' | 'error'

/** What a speaker's channel is doing right now, for providers without partial text. */
export type SpeakerActivity = 'speaking' | 'processing' | 'idle'

export type SpeakerRole = 'doctor' | 'pds'

/** Stable identity of a segment so the socket echo and the room broadcast never duplicate a line. */
export function segmentKey(segment: TranscriptionSegment): string {
  return `${segment.speaker ?? 'unknown'}|${segment.start}|${segment.end}|${segment.text}`
}

/** Appends `incoming` to `current` unless an identical segment is already there. */
export function mergeSegments(current: TranscriptionSegment[], incoming: TranscriptionSegment[]): TranscriptionSegment[] {
  if (!incoming.length) return current
  const known = new Set(current.map(segmentKey))
  const fresh = incoming.filter((segment) => {
    const key = segmentKey(segment)
    if (known.has(key)) return false
    known.add(key)
    return true
  })
  return fresh.length ? [...current, ...fresh] : current
}

/**
 * Who is speaking on each captured stream. The local mic is the logged-in staff member;
 * the remote (Chime) track is the other side of the call: the patient for a doctor, the
 * doctor for a nurse / operator sitting next to the patient.
 */
export function speakersFor(role: SpeakerRole): { local: TranscriptionSpeaker; remote: TranscriptionSpeaker } {
  return role === 'doctor' ? { local: 'doctor', remote: 'patient' } : { local: 'pds', remote: 'doctor' }
}

/** Groups consecutive segments of the same speaker into one paragraph for readability. */
export type TranscriptParagraph = { speaker: TranscriptionSpeaker; start: string; end: string; at?: string; texts: string[] }

export function groupBySpeaker(segments: TranscriptionSegment[]): TranscriptParagraph[] {
  const paragraphs: TranscriptParagraph[] = []
  for (const segment of segments) {
    const speaker = segment.speaker ?? 'unknown'
    const last = paragraphs[paragraphs.length - 1]
    if (last && last.speaker === speaker) {
      last.texts.push(segment.text)
      last.end = segment.end
    } else {
      paragraphs.push({ speaker, start: segment.start, end: segment.end, at: segment.at, texts: [segment.text] })
    }
  }
  return paragraphs
}

/**
 * What to print next to a paragraph: the wall-clock time of day when we have it
 * (live capture), otherwise the offset since the consultation started (uploads, recordings).
 */
export function formatSegmentTime(paragraph: Pick<TranscriptParagraph, 'start' | 'at'>, locale?: string): string {
  if (paragraph.at) {
    const date = new Date(paragraph.at)
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  }
  return paragraph.start
}

export function formatElapsed(totalSeconds: number): string {
  const seconds = Math.max(0, Math.floor(totalSeconds))
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return `${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`
}

export type SpeakerNames = Partial<Record<TranscriptionSpeaker, string>>

/**
 * Display names for the people on the call, in the same form as the live-page topbar
 * ("Dr. Surname Name" for the doctor, "Surname Name" otherwise). Missing people map to
 * nothing so the caller can fall back to the role label.
 */
export function speakerNames(consultation?: Pick<Consultation, 'medecin' | 'pds' | 'patient'> | null): SpeakerNames {
  const fullName = (person?: { userInfo?: { surname?: string; name?: string } } | null) =>
    [person?.userInfo?.surname, person?.userInfo?.name].filter(Boolean).join(' ').trim()
  const doctor = fullName(consultation?.medecin)
  const pds = fullName(consultation?.pds)
  const patient = fullName(consultation?.patient)
  return {
    ...(doctor ? { doctor: `Dr. ${doctor}` } : {}),
    ...(pds ? { pds } : {}),
    ...(patient ? { patient } : {}),
  }
}
